import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { menuItemStyles } from '@utils/styles';
import CustomDatePicker from '../../../../components/CustomDatePicker/CustomDatePicker';
import CustomTextField from '../../../../components/CustomTextField/CustomTextField';
import CustomButton from '../../../../components/CustomButton/CustomButton';
import {
  DealDetailsRequestPayload,
  InvestorContributionSplit,
  useDealDetailsMutation,
  useLazyGetDealsQuery,
} from '@api/deals';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import { useDispatch } from 'react-redux';
import { useLazyGetInvestorsQuery } from '@api/investments';
import {
  createFormattedPercentValue,
  handlePercentageChange,
  stripPercentSymbol,
} from '@utils/percentageInputsUtils';
import { formatDateToYYYYMMDD } from '@utils/common';
import DealSetupFooter from './components/DealSetupFooter';

interface InvestorData {
  id: number;
  investorId: string;
  investorName: string;
  investorContribution: string;
  investorReturnCap: string;
}

interface FormState {
  pricingMethodology: 'SIMPLE_INTEREST' | 'DYNAMIC_RETURN_CAP' | 'DYNAMIC_RETURN_CAP_V2' | 'CUSTOM';
  interestOnUnderspendAmount: string;
  pricingData: {
    interestPercentage: string;
    firstMonthCap: string;
    incrementalReturnCap: string;
    customPricing: string;
  };
  investorRows: InvestorData[];
  validityDates: {
    startDate: string;
    endDate: string;
  };
  cohortMonthOffset: number | null;
  status: 'ACTIVE' | 'NONE';
}

const COHORT_MONTH_OFFSET_OPTIONS = [-1, 0, 1];

const PRICING_METHODS = [
  { label: 'Simple Interest', value: 'SIMPLE_INTEREST' },
  { label: 'Dynamic Return Cap', value: 'DYNAMIC_RETURN_CAP' },
  { label: 'Dynamic Return Cap V2', value: 'DYNAMIC_RETURN_CAP_V2' },
  { label: 'Custom Return Cap', value: 'CUSTOM' },
];

const CUSTOM_PRICING_TYPES = new Set(['CUSTOM', 'CUSTOM_FLIFF', 'CUSTOM_PLAYSOME']);

interface DealDetailsProps {
  gameId: string;
  handleCancelClick: () => void;
  dealUpdatedDetailsComponent: JSX.Element;
  fetchDealHistory: (gameId: string) => void;
}

interface TouchedState {
  pricingData: {
    interestPercentage: boolean;
    firstMonthCap: boolean;
    incrementalReturnCap: boolean;
    customPricing: boolean;
  };
  validityDates: {
    startDate: boolean;
    endDate: boolean;
  };
}

interface ErrorState {
  pricingData: {
    interestPercentage: boolean | string;
    firstMonthCap: boolean | string;
    incrementalReturnCap: boolean | string;
    customPricing: boolean | string;
  };
  validityDates: {
    startDate: boolean | string;
    endDate: boolean | string;
    validate: string;
  };
}

const initialTouchedState = {
  pricingData: {
    interestPercentage: false,
    firstMonthCap: false,
    incrementalReturnCap: false,
    customPricing: false,
  },
  validityDates: {
    startDate: false,
    endDate: false,
  },
};

const initialErrorState = {
  pricingData: {
    interestPercentage: false,
    firstMonthCap: false,
    incrementalReturnCap: false,
    customPricing: false,
  },
  validityDates: {
    startDate: false,
    endDate: false,
    validate: '',
  },
};

const DealDetails: FC<DealDetailsProps> = (props) => {
  const { gameId, handleCancelClick, dealUpdatedDetailsComponent, fetchDealHistory } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();

  const [formState, setFormState] = useState<FormState>({
    pricingMethodology: 'SIMPLE_INTEREST',
    interestOnUnderspendAmount: '',
    pricingData: {
      interestPercentage: '',
      firstMonthCap: '',
      incrementalReturnCap: '',
      customPricing: '',
    },
    investorRows: [
      {
        id: 1,
        investorId: '',
        investorName: '',
        investorContribution: '',
        investorReturnCap: '',
      },
    ],
    validityDates: {
      startDate: '',
      endDate: '',
    },
    cohortMonthOffset: 0,
    status: 'NONE',
  });
  const [touched, setTouched] = useState<TouchedState>(initialTouchedState);
  const [errorState, setErrorState] = useState<ErrorState>(initialErrorState);
  const [investors, setInvestors] = useState<string[]>([]);
  const [investorsLoaded, setInvestorsLoaded] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);

  const [getDeals] = useLazyGetDealsQuery();
  const [getInvestors] = useLazyGetInvestorsQuery();
  const [createDealDetails] = useDealDetailsMutation();

  useEffect(() => {
    if (!gameId || !investorsLoaded) return;
    fetchDealDetails(gameId);
  }, [gameId, investorsLoaded]);

  useEffect(() => {
    fetchInvestors();
  }, []);

  useEffect(() => {
    validateForm();
  }, [formState]);

  const fetchInvestors = async () => {
    try {
      const { data } = await getInvestors().unwrap();
      setInvestorsLoaded(true);
      if (data.investors?.length) {
        setInvestors(data.investors);
      }
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || 'Fetching Investors failed.',
        }),
      );
    }
  };

  const compareValidityDates = (startDate: string | null, endDate: string | null): string => {
    if (startDate === null || endDate === null) {
      return '';
    }

    if (!startDate || !endDate) {
      return '';
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) {
      return 'Start date should be before end date';
    }
    return '';
  };

  const validateForm = () => {
    const updatedErrorState = { ...errorState };
    if (formState.pricingMethodology === 'SIMPLE_INTEREST') {
      updatedErrorState.pricingData.interestPercentage = !formState.pricingData.interestPercentage;
    } else if (
      formState.pricingMethodology === 'DYNAMIC_RETURN_CAP' ||
      formState.pricingMethodology === 'DYNAMIC_RETURN_CAP_V2'
    ) {
      updatedErrorState.pricingData.firstMonthCap = !formState.pricingData.firstMonthCap;
      updatedErrorState.pricingData.incrementalReturnCap =
        !formState.pricingData.incrementalReturnCap;
    } else if (formState.pricingMethodology === 'CUSTOM') {
      updatedErrorState.pricingData.customPricing = !formState.pricingData.customPricing;
      if (formState.pricingData.customPricing) {
        try {
          JSON.parse(formState.pricingData.customPricing);
        } catch {
          updatedErrorState.pricingData.customPricing = 'Invalid JSON format';
        }
      }
    }

    updatedErrorState.validityDates.validate = compareValidityDates(
      formState.validityDates.startDate,
      formState.validityDates.endDate,
    );

    if (formState.status === 'ACTIVE') {
      updatedErrorState.validityDates.startDate = !formState.validityDates.startDate;
      updatedErrorState.validityDates.endDate = !formState.validityDates.endDate;
    } else {
      updatedErrorState.validityDates.startDate = false;
      updatedErrorState.validityDates.endDate = false;
    }

    setErrorState(updatedErrorState);
  };

  const fetchDealDetails = async (id: string) => {
    try {
      const response = await getDeals({
        gameId: id,
        isActive: true,
      }).unwrap();
      if (response?.data?.length) {
        const dealData = response.data[0];

        // Create a new form state to update
        const updatedFormState = { ...formState };

        if (dealData.pricingMethodology) {
          updatedFormState.pricingMethodology = dealData.pricingMethodology as
            | 'SIMPLE_INTEREST'
            | 'DYNAMIC_RETURN_CAP'
            | 'DYNAMIC_RETURN_CAP_V2'
            | 'CUSTOM';
        }

        // Update status if available
        if (dealData.status) {
          updatedFormState.status = dealData.status as 'ACTIVE' | 'NONE';
        }

        // Update cohort month if available
        if (!isNaN(dealData.cohortMonth)) {
          updatedFormState.cohortMonthOffset = dealData.cohortMonth;
        }

        // Update investor rows if available
        if (dealData.investorContributionSplits.length) {
          updatedFormState.investorRows = dealData.investorContributionSplits.map(
            (investor: InvestorContributionSplit, index: number) => ({
              id: index + 1,
              investorId: investor.id,
              investorName: investor.investor,
              investorContribution: createFormattedPercentValue(
                investor.contributionPercentage * 100,
              ),
              investorReturnCap: createFormattedPercentValue(investor.returnCap * 100),
            }),
          );
        } else {
          updatedFormState.investorRows = investors.map((investor, index) => ({
            id: index + 1,
            investorId: '',
            investorName: investor,
            investorContribution: '',
            investorReturnCap: '',
          }));
        }

        // Update underspend interest amount
        updatedFormState.interestOnUnderspendAmount = createFormattedPercentValue(
          dealData.underspentInterest * 100,
        );

        // Update pricing data based on methodology
        const interest = JSON.parse(dealData.interest);
        if (dealData.pricingMethodology === 'SIMPLE_INTEREST') {
          updatedFormState.pricingData.interestPercentage = interest['SIMPLE_INTEREST_RATE']
            ? createFormattedPercentValue(parseFloat(interest['SIMPLE_INTEREST_RATE']) * 100)
            : '';
        } else if (
          dealData.pricingMethodology === 'DYNAMIC_RETURN_CAP' ||
          dealData.pricingMethodology === 'DYNAMIC_RETURN_CAP_V2'
        ) {
          updatedFormState.pricingData.firstMonthCap = interest['BASE_RATE']
            ? createFormattedPercentValue(parseFloat(interest['BASE_RATE']) * 100)
            : '';
          updatedFormState.pricingData.incrementalReturnCap = interest['DYNAMIC_RATE']
            ? createFormattedPercentValue(parseFloat(interest['DYNAMIC_RATE']) * 100)
            : '';
        } else {
          try {
            updatedFormState.pricingData.customPricing = JSON.stringify(
              JSON.parse(dealData.interest),
            );
          } catch {
            updatedFormState.pricingData.customPricing = '';
          }
        }

        // Update validity dates
        updatedFormState.validityDates = {
          startDate: dealData?.startDate || '',
          endDate: dealData?.endDate || '',
        };

        // Update form state with all changes
        setFormState(updatedFormState);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
    }
  };

  const updateFormField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    if (['startDate', 'endDate'].includes(field)) {
      setTouched((prevState) => ({
        ...prevState,
        validityDates: {
          ...prevState.validityDates,
          [field]: true,
        },
      }));
    }
    setFormState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const updatePricingData = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof FormState['pricingData'],
  ) => {
    if (['interestPercentage', 'firstMonthCap', 'incrementalReturnCap'].includes(field)) {
      handlePercentageChange(e, (formattedValue) => {
        setFormState((prevState) => ({
          ...prevState,
          pricingData: {
            ...prevState.pricingData,
            [field]: formattedValue,
          },
        }));
      });

      return;
    }

    const value = e.target.value;

    setFormState((prevState) => ({
      ...prevState,
      pricingData: {
        ...prevState.pricingData,
        [field]: value,
      },
    }));
  };

  const updateUnderspendInterest = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    handlePercentageChange(e, (result) => {
      setFormState((prevState) => ({
        ...prevState,
        interestOnUnderspendAmount: result,
      }));
    });
  };

  const updateInvestorField = (
    id: number,
    field: keyof Omit<InvestorData, 'id'>,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>,
  ) => {
    const value = e.target.value;

    if (field === 'investorContribution' || field === 'investorReturnCap') {
      handlePercentageChange(
        e as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        (result) => {
          setFormState((prevState) => ({
            ...prevState,
            investorRows: prevState.investorRows.map((row) => {
              if (row.id !== id) return row;
              return { ...row, [field]: result };
            }),
          }));
        },
      );
    } else {
      setFormState((prevState) => {
        const updatedRows = prevState.investorRows.map((row) => {
          if (row.id !== id) return row;

          return { ...row, [field]: value };
        });

        return {
          ...prevState,
          investorRows: updatedRows,
        };
      });
    }
  };

  const handleDeleteInvestor = (id: number) => {
    setFormState((prevState) => ({
      ...prevState,
      investorRows: prevState.investorRows.filter((row) => row.id !== id),
    }));
  };

  const handleAddInvestor = () => {
    const newInvestor: InvestorData = {
      id: Date.now(),
      investorId: '',
      investorName: '',
      investorContribution: '',
      investorReturnCap: '',
    };

    setFormState((prevState) => ({
      ...prevState,
      investorRows: [...prevState.investorRows, newInvestor],
    }));
  };

  const generateInterestPayload = () => {
    if (formState.pricingMethodology === 'SIMPLE_INTEREST') {
      return {
        SIMPLE_INTEREST_RATE: (
          stripPercentSymbol(formState.pricingData.interestPercentage) / 100
        ).toFixed(4),
      };
    } else if (
      formState.pricingMethodology === 'DYNAMIC_RETURN_CAP' ||
      formState.pricingMethodology === 'DYNAMIC_RETURN_CAP_V2'
    ) {
      return {
        BASE_RATE: (stripPercentSymbol(formState.pricingData.firstMonthCap) / 100).toFixed(4),
        DYNAMIC_RATE: (
          stripPercentSymbol(formState.pricingData.incrementalReturnCap) / 100
        ).toFixed(4),
      };
    } else if (formState.pricingMethodology === 'CUSTOM') {
      return JSON.parse(formState.pricingData.customPricing) || {};
    }

    return {};
  };

  const handleUTCDate = (dateString: string): string | null => {
    if (!dateString) return null;

    try {
      if (dateString.includes('T')) {
        const date = new Date(dateString);
        return formatDateToYYYYMMDD(date);
      } else {
        return dateString;
      }
    } catch {
      return null;
    }
  };

  const handleSaveClick = async () => {
    const payload: DealDetailsRequestPayload = {
      gameId: gameId,
      pricingMethodology: formState.pricingMethodology,
      interest: generateInterestPayload(),
      status: formState.status,
      startDate: handleUTCDate(formState.validityDates.startDate),
      endDate: handleUTCDate(formState.validityDates.endDate),
      underspentInterest: stripPercentSymbol(formState.interestOnUnderspendAmount) / 100,
      cohortMonth:
        formState.cohortMonthOffset && isNaN(formState.cohortMonthOffset)
          ? null
          : formState.cohortMonthOffset,
      investorContributionSplits:
        formState.investorRows.map((row) => ({
          investor: row.investorName,
          contributionPercentage: stripPercentSymbol(row.investorContribution) / 100,
          returnCap: stripPercentSymbol(row.investorReturnCap) / 100,
        })) || [],
    };

    try {
      setLoader(true);
      await createDealDetails(payload).unwrap();
      fetchDealHistory(gameId);
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || 'Updating Deal Details failed. Please try again.',
        }),
      );
    } finally {
      setLoader(false);
      fetchDealDetails(gameId);
    }
  };

  const isFormValid = (): boolean => {
    if (!formState.pricingMethodology) {
      return false;
    }

    if (
      formState.pricingMethodology === 'SIMPLE_INTEREST' &&
      formState.pricingData.interestPercentage === ''
    ) {
      return false;
    }

    if (
      (formState.pricingMethodology === 'DYNAMIC_RETURN_CAP' ||
        formState.pricingMethodology === 'DYNAMIC_RETURN_CAP_V2') &&
      (formState.pricingData.firstMonthCap === '' ||
        formState.pricingData.incrementalReturnCap === '')
    ) {
      return false;
    }

    if (formState.pricingMethodology === 'CUSTOM') {
      if (formState.pricingData.customPricing === '') return false;
      try {
        JSON.parse(formState.pricingData.customPricing);
      } catch {
        return false;
      }
    }

    if (
      formState.status === 'ACTIVE' &&
      (!formState.validityDates.startDate ||
        !formState.validityDates.endDate ||
        formState.cohortMonthOffset === null)
    ) {
      return false;
    }

    if (
      !!compareValidityDates(formState.validityDates.startDate, formState.validityDates.endDate)
    ) {
      return false;
    }

    const totalContribution = formState.investorRows.reduce(
      (sum, investor) => sum + (stripPercentSymbol(investor.investorContribution) || 0),
      0,
    );
    if (totalContribution !== 100) {
      return false;
    }

    return true;
  };

  const checkPricingMethodology = (value: string) => {
    // Direct comparison for standard types (most common cases)
    if (value === formState.pricingMethodology) {
      return true;
    }

    // Handle custom types efficiently with Set lookup
    if (CUSTOM_PRICING_TYPES.has(value)) {
      return CUSTOM_PRICING_TYPES.has(formState.pricingMethodology);
    }

    return false;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        id="game-details"
        sx={{
          flex: '1',
          pr: '0.5rem',
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '1rem',
          pb: '1rem',
        }}
      >
        {loader ? (
          <Box
            sx={{
              gridColumn: '1 / -1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress
              style={{
                width: '4rem',
                height: '4rem',
                color: theme.palette.primary.main,
              }}
            />
          </Box>
        ) : (
          <>
            {/* Pricing Methodologies */}
            <Box
              sx={{
                gridColumn: '1 / -1',
              }}
            >
              <Typography
                sx={{
                  fontWeight: '700',
                  fontSize: '1rem',
                  marginBottom: '0.75rem',
                }}
              >
                Pricing Methodologies
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '1.75rem',
                  pl: '0.25rem',
                }}
              >
                {PRICING_METHODS.map(({ label, value }) => (
                  <FormControlLabel
                    key={value}
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: '700',
                        fontSize: '0.875rem',
                      },
                    }}
                    control={
                      <Checkbox
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        checked={checkPricingMethodology(value)}
                        onChange={() =>
                          updateFormField(
                            'pricingMethodology',
                            value as
                              | 'SIMPLE_INTEREST'
                              | 'DYNAMIC_RETURN_CAP'
                              | 'DYNAMIC_RETURN_CAP_V2'
                              | 'CUSTOM',
                          )
                        }
                        sx={{
                          color: '#2F736E',
                          '&.Mui-checked': {
                            color: '#2F736E',
                          },
                          '& .MuiSvgIcon-root': {
                            borderRadius: '50%',
                          },
                        }}
                        icon={
                          <span
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              border: '2px solid #2F736E',
                              display: 'inline-block',
                            }}
                          ></span>
                        }
                        checkedIcon={
                          <span
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              border: '2px solid #2F736E',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <span
                              style={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: '#2F736E',
                              }}
                            ></span>
                          </span>
                        }
                      />
                    }
                    label={label}
                  />
                ))}
              </Box>
              {formState.pricingMethodology === 'SIMPLE_INTEREST' && (
                <CustomTextField
                  id="interest-percentage"
                  label="Interest %"
                  required
                  error={
                    touched.pricingData.interestPercentage &&
                    !!errorState.pricingData.interestPercentage
                  }
                  customStyle={{
                    width: isMobile ? '100%' : '50%',
                    margin: '1.25rem 0rem',
                  }}
                  value={formState.pricingData.interestPercentage}
                  onFieldChange={(e) => updatePricingData(e, 'interestPercentage')}
                  onBlur={() => {
                    setTouched({
                      ...touched,
                      pricingData: {
                        ...touched.pricingData,
                        interestPercentage: true,
                      },
                    });
                  }}
                />
              )}

              {['DYNAMIC_RETURN_CAP', 'DYNAMIC_RETURN_CAP_V2'].includes(
                formState.pricingMethodology,
              ) && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '1rem',
                  }}
                >
                  <CustomTextField
                    required
                    id="first-month-cap"
                    label="First Month Cap"
                    customStyle={{
                      width: isMobile ? '100%' : '25%',
                      margin: '1.25rem 0rem',
                    }}
                    value={formState.pricingData.firstMonthCap}
                    error={
                      touched.pricingData.firstMonthCap && !!errorState.pricingData.firstMonthCap
                    }
                    onFieldChange={(e) => updatePricingData(e, 'firstMonthCap')}
                    onBlur={() => {
                      setTouched({
                        ...touched,
                        pricingData: {
                          ...touched.pricingData,
                          firstMonthCap: true,
                        },
                      });
                    }}
                  />
                  <CustomTextField
                    required
                    id="incremental-return-cap"
                    label="Incremental Return Cap"
                    customStyle={{
                      width: isMobile ? '100%' : '25%',
                      margin: '1.25rem 0rem',
                    }}
                    value={formState.pricingData.incrementalReturnCap}
                    error={
                      touched.pricingData.incrementalReturnCap &&
                      !!errorState.pricingData.incrementalReturnCap
                    }
                    onFieldChange={(e) => updatePricingData(e, 'incrementalReturnCap')}
                    onBlur={() => {
                      setTouched({
                        ...touched,
                        pricingData: {
                          ...touched.pricingData,
                          incrementalReturnCap: true,
                        },
                      });
                    }}
                  />
                </Box>
              )}
              {['CUSTOM', 'CUSTOM_FLIFF', 'CUSTOM_PLAYSOME'].includes(
                formState.pricingMethodology,
              ) && (
                <CustomTextField
                  id="custom-pricing"
                  label="Custom Pricing"
                  required
                  error={
                    touched.pricingData.customPricing && !!errorState.pricingData.customPricing
                  }
                  helperText={
                    typeof errorState.pricingData.customPricing === 'string'
                      ? errorState.pricingData.customPricing
                      : ''
                  }
                  customStyle={{
                    width: isMobile ? '100%' : '50%',
                    margin: '1.25rem 0rem',
                  }}
                  value={formState.pricingData.customPricing}
                  onFieldChange={(e) => updatePricingData(e, 'customPricing')}
                  onBlur={() => {
                    setTouched({
                      ...touched,
                      pricingData: {
                        ...touched.pricingData,
                        customPricing: true,
                      },
                    });
                  }}
                />
              )}
            </Box>

            {/* Validity */}
            <Box>
              <Stack direction={isMobile ? 'column' : 'row'}>
                <Box>
                  <Typography
                    fontSize={'1rem'}
                    fontWeight={700}
                    sx={{ color: '#162936', marginBottom: '1.25rem' }}
                  >
                    Validity
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '1rem',
                    }}
                  >
                    <Box width={'100%'}>
                      <CustomDatePicker
                        required={formState.status === 'ACTIVE'}
                        label="Start Date"
                        format={'YYYY-MM-DD'}
                        value={formState.validityDates.startDate}
                        onChange={(value) =>
                          updateFormField('validityDates', {
                            ...formState.validityDates,
                            startDate: value,
                          })
                        }
                        fullWidth={true}
                      />
                    </Box>

                    <Box width={'100%'}>
                      <CustomDatePicker
                        label="End Date"
                        required={formState.status === 'ACTIVE'}
                        format={'YYYY-MM-DD'}
                        value={formState.validityDates.endDate}
                        onChange={(value) =>
                          updateFormField('validityDates', {
                            ...formState.validityDates,
                            endDate: value,
                          })
                        }
                        fullWidth={true}
                      />
                    </Box>
                  </Box>

                  {errorState.validityDates.validate && (
                    <Typography
                      fontSize={'0.75rem'}
                      fontWeight={500}
                      sx={{
                        color: '#FF0000',
                        marginTop: '0.5rem',
                      }}
                    >
                      {errorState.validityDates.validate}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>

            {/* Status */}
            <Box fontSize={'1rem'} fontWeight={700} sx={{ color: '#162936' }}>
              <Typography
                fontSize={'1rem'}
                fontWeight={700}
                sx={{ color: '#162936', marginBottom: '1.25rem' }}
              >
                Status
              </Typography>
              <FormControl fullWidth>
                <InputLabel required id="status">
                  Status
                </InputLabel>
                <Select
                  labelId="status"
                  value={formState.status}
                  required
                  label="Status"
                  placeholder="Status"
                  onChange={(e) => updateFormField('status', e.target.value as 'ACTIVE' | 'NONE')}
                  fullWidth
                  sx={{
                    height: '56px',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6C6C6C',
                    },
                  }}
                >
                  {['ACTIVE', 'NONE'].map((option, index) => (
                    <MenuItem
                      key={`${index}--${option}`}
                      value={option}
                      sx={{
                        ...menuItemStyles,
                      }}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Cohort Month Offset */}
            <Box fontSize={'1rem'} fontWeight={700} sx={{ color: '#162936' }}>
              <Typography
                fontSize={'1rem'}
                fontWeight={700}
                sx={{ color: '#162936', marginBottom: '1.25rem' }}
              >
                Cohort Month Offset
              </Typography>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="cohort-month-offset" required={formState.status === 'ACTIVE'}>
                  Cohort Month Offset
                </InputLabel>
                <Select
                  required={formState.status === 'ACTIVE'}
                  labelId="cohort-month-offset"
                  value={formState.cohortMonthOffset}
                  label="Cohort Month Offset"
                  onChange={(e) => updateFormField('cohortMonthOffset', e.target.value as number)}
                  fullWidth
                  sx={{
                    height: '56px',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6C6C6C',
                    },
                  }}
                >
                  {COHORT_MONTH_OFFSET_OPTIONS.map((option, index) => (
                    <MenuItem key={`${index}--${option}`} value={option} sx={{ ...menuItemStyles }}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Interest on Underspent Amount */}
            <Box>
              <Typography
                fontSize={'1rem'}
                fontWeight={700}
                sx={{ color: '#162936', marginBottom: '1.25rem' }}
              >
                Interest on Underspent Amount
              </Typography>
              <CustomTextField
                id="interest-on-underspent-amount"
                label="Interest on Underspent Amount"
                customStyle={{
                  width: '100%',
                }}
                value={formState.interestOnUnderspendAmount}
                onFieldChange={(e) => updateUnderspendInterest(e)}
              />
            </Box>

            {/* Investor Contribution Split */}
            <Box
              sx={{
                gridColumn: '1 / -1',
              }}
            >
              <Typography
                fontSize={'1rem'}
                fontWeight={700}
                sx={{ color: '#162936', marginBottom: '1.25rem' }}
              >
                Investor Contribution Split *
              </Typography>
              {formState.investorRows.map((row) => (
                <Box
                  key={row.id}
                  sx={{
                    display: 'grid',
                    gap: '1rem',
                    rowGap: '1.5rem',
                    gridTemplateColumns: '1fr 1fr 1fr 40px',
                    marginBottom: '0.75rem',
                  }}
                >
                  {/* Investor Name */}
                  <Box
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '100%',
                      },
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel required id={`investor-name-${row.id}`}>
                        Investor
                      </InputLabel>
                      <Select
                        required
                        labelId={`investor-name-${row.id}`}
                        value={row.investorName}
                        label="Investor"
                        placeholder="Investor"
                        onChange={(e) => {
                          updateInvestorField(row.id, 'investorName', e);
                        }}
                        fullWidth
                        sx={{
                          height: '56px',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          width: '50%',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#6C6C6C',
                          },
                        }}
                      >
                        {investors.map((option, index) => (
                          <MenuItem
                            key={`${index}--${option}`}
                            value={option}
                            sx={{
                              ...menuItemStyles,
                            }}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Investor Contribution */}
                  <CustomTextField
                    id={`investor-contribution-${row.id}`}
                    label="Investor Contribution"
                    required
                    customStyle={{ flex: 1 }}
                    value={row.investorContribution}
                    onFieldChange={(e) => updateInvestorField(row.id, 'investorContribution', e)}
                  />

                  {/* Investor Return Cap */}
                  {row.investorName === 'PVX' ? (
                    <Box></Box>
                  ) : (
                    <CustomTextField
                      id={`investor-return-cap-${row.id}`}
                      label="Investor Return Cap"
                      customStyle={{ flex: 1 }}
                      value={row.investorReturnCap}
                      onFieldChange={(e) => updateInvestorField(row.id, 'investorReturnCap', e)}
                    />
                  )}

                  <Box
                    sx={{
                      height: 'inherit',
                      alignItems: 'center',
                      display: 'flex',
                    }}
                  >
                    <DeleteOutlinedIcon
                      sx={{
                        cursor: formState.investorRows.length === 1 ? 'not-allowed' : 'pointer',
                        fill: formState.investorRows.length === 1 ? '#6C6C6C' : '#2F736E',
                        width: '2rem',
                      }}
                      onClick={() => {
                        if (formState.investorRows.length === 1) return;
                        handleDeleteInvestor(row.id);
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
            <Box>
              <CustomButton
                color={'#F6F8F9'}
                backgroundColor={'#2F736E'}
                name="Add New Investor"
                startIcon={AddIcon}
                onClick={handleAddInvestor}
                disabled={formState.investorRows.length === investors.length}
              />
            </Box>

            <Typography
              pb={'1rem'}
              fontSize={'0.875rem'}
              color={'#6C6C6C'}
              fontWeight={400}
              sx={{ gridColumn: '1 / -1' }}
            >
              <Typography
                component={'span'}
                fontSize={'0.875rem'}
                color={'#6C6C6C'}
                fontWeight={700}
              >
                Note:
              </Typography>{' '}
              Total investor contribution must equal 100%.
            </Typography>
          </>
        )}
      </Box>
      <DealSetupFooter
        dealUpdatedDetailsComponent={dealUpdatedDetailsComponent}
        onCancel={handleCancelClick}
        onSave={handleSaveClick}
        isSubmitEnabled={isFormValid()}
      />
    </Box>
  );
};

export default DealDetails;
